// hazardous-chemicals/components/SdsUploadDialog.tsx
import React, { useRef, useState } from 'react';
import { X, Upload, FileText, Loader2, AlertTriangle, FlaskConical } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import type { Chemical } from '../../../../types/chemical.types';

interface SdsUploadItem {
    chemical: Chemical;
    file: File | null;
    uploading: boolean;
    uploaded: boolean;
    error: string | null;
}

export const SdsUploadDialog: React.FC<{
    open: boolean;
    chemicals: Chemical[];
    onClose: () => void;
    onUpload: (chemicalId: string, file: File) => Promise<void>;
}> = ({ open, chemicals, onClose, onUpload }) => {
    const [items, setItems] = useState<SdsUploadItem[]>(() =>
        chemicals.map((c) => ({ chemical: c, file: null, uploading: false, uploaded: false, error: null })),
    );
    const [overallUploading, setOverallUploading] = useState(false);
    const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    // Reset items when chemicals change
    React.useEffect(() => {
        setItems(chemicals.map((c) => ({ chemical: c, file: null, uploading: false, uploaded: false, error: null })));
    }, [chemicals]);

    const handleFileSelect = (chemicalId: string, file: File | null) => {
        setItems((prev) =>
            prev.map((item) =>
                item.chemical.id === chemicalId ? { ...item, file, error: null } : item,
            ),
        );
    };

    const handleUploadAll = async () => {
        const itemsToUpload = items.filter((item) => item.file && !item.uploaded);
        if (itemsToUpload.length === 0) return;

        setOverallUploading(true);

        for (const item of itemsToUpload) {
            if (!item.file) continue;

            setItems((prev) =>
                prev.map((i) => (i.chemical.id === item.chemical.id ? { ...i, uploading: true, error: null } : i)),
            );

            try {
                await onUpload(item.chemical.id, item.file);
                setItems((prev) =>
                    prev.map((i) =>
                        i.chemical.id === item.chemical.id ? { ...i, uploading: false, uploaded: true } : i,
                    ),
                );
            } catch (err: any) {
                setItems((prev) =>
                    prev.map((i) =>
                        i.chemical.id === item.chemical.id
                            ? { ...i, uploading: false, error: err.message || 'Upload failed' }
                            : i,
                    ),
                );
            }
        }

        setOverallUploading(false);
    };

    const readyCount = items.filter((i) => i.file && !i.uploaded).length;
    const uploadedCount = items.filter((i) => i.uploaded).length;
    const allDone = items.length > 0 && items.every((i) => i.uploaded || i.chemical.sds);

    const handleClose = () => {
        if (!overallUploading) {
            onClose();
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <AlertDialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200 max-h-[80vh] flex flex-col">
                <AlertDialogHeader className="p-6 pb-4 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Upload size={20} className="text-primary" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Upload Safety Data Sheets</AlertDialogTitle>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={overallUploading}
                            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                        Upload PDF SDS files for {items.length} chemical{items.length !== 1 ? 's' : ''}.
                        Only PDF files up to 20MB are accepted.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {/* Chemical list with file pickers */}
                <div className="px-6 pb-2 overflow-y-auto flex-1 min-h-0">
                    <div className="space-y-3">
                        {items.map((item) => (
                            <div
                                key={item.chemical.id}
                                className={`rounded-xl border p-3.5 transition-colors ${
                                    item.uploaded
                                        ? 'border-emerald-200 bg-emerald-50/50'
                                        : item.error
                                        ? 'border-red-200 bg-red-50/50'
                                        : item.file
                                        ? 'border-blue-200 bg-blue-50/30'
                                        : 'border-gray-100 bg-gray-50/50'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        item.uploaded ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {item.uploaded ? <FileText size={16} /> : <FlaskConical size={16} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-gray-900 truncate">{item.chemical.name}</p>
                                            {item.chemical.sds && !item.uploaded && (
                                                <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Has SDS</span>
                                            )}
                                            {item.uploaded && (
                                                <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Uploaded</span>
                                            )}
                                            {item.uploading && (
                                                <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                                                    <Loader2 size={10} className="animate-spin" />
                                                    Uploading
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{item.chemical.location || '—'} · {item.chemical.unNumber || 'No UN Number'}</p>

                                        {item.error && (
                                            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                                                <AlertTriangle size={12} />
                                                {item.error}
                                            </p>
                                        )}

                                        {!item.chemical.sds && !item.uploaded && (
                                            <div className="mt-2">
                                                {item.file ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 min-w-0 flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-1.5">
                                                            <FileText size={14} className="text-blue-500 flex-shrink-0" />
                                                            <span className="text-xs text-gray-700 truncate">{item.file.name}</span>
                                                            <span className="text-[10px] text-gray-400 flex-shrink-0">{(item.file.size / (1024 * 1024)).toFixed(1)} MB</span>
                                                        </div>
                                                        <button
                                                            onClick={() => handleFileSelect(item.chemical.id, null)}
                                                            disabled={item.uploading}
                                                            className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => fileInputRefs.current[item.chemical.id]?.click()}
                                                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <Upload size={14} />
                                                        Select PDF file
                                                    </button>
                                                )}
                                                <input
                                                    ref={(el) => { fileInputRefs.current[item.chemical.id] = el; }}
                                                    type="file"
                                                    accept=".pdf,application/pdf"
                                                    className="hidden"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0] || null;
                                                        if (file && !file.type.includes('pdf')) {
                                                            return;
                                                        }
                                                        handleFileSelect(item.chemical.id, file);
                                                        if (e.target) e.target.value = '';
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <AlertDialogFooter className="px-6 py-4 gap-2 border-t border-gray-100 flex-shrink-0">
                    <AlertDialogCancel
                        className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                        disabled={overallUploading}
                        onClick={handleClose}
                    >
                        {allDone ? 'Done' : 'Cancel'}
                    </AlertDialogCancel>
                    {readyCount > 0 && (
                        <Button
                            className="bg-primary hover:bg-primary/90 text-white h-10 rounded-xl text-sm font-bold px-5"
                            onClick={handleUploadAll}
                            disabled={overallUploading}
                        >
                            {overallUploading ? (
                                <span className="inline-flex items-center gap-1.5">
                                    <Loader2 size={14} className="animate-spin" />
                                    Uploading...
                                </span>
                            ) : (
                                `Upload ${readyCount} SDS file${readyCount !== 1 ? 's' : ''}`
                            )}
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
